import { DefinedPropertyObject } from "../../api/GenericTypes";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { EntityFilterSideBar } from "../../common/sidebar/filtersidebar/EntityFilterBar";
import { SplitDatePickerFormField } from "../../common/formfields/DatePickerFormField/SplitDatePickerFormField";
import {
  Announcement,
  AnnouncementFilters,
  AnnouncementType,
  AnnouncementTypes,
  announcementFieldLabels,
} from "../../api/Announcements";
import { SelectFormField } from "../../common/formfields/SelectFormField";
import { ToggleFormField } from "../../common/formfields/ToggleFormField";
import { useContext } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { toValidDate } from "../../common/datetime/DateTimeFormatter";

type AnnouncementTypeSelect = { id: AnnouncementType; name: AnnouncementType };

export interface AnnouncementFilterForm extends ICombinedModificationRecordEntityForm {
  types?: AnnouncementTypeSelect[] | undefined | null;
  current?: boolean | undefined | null;
  startDateFrom?: Date | undefined | null;
  startDateTo?: Date | undefined | null;
  endDateFrom?: Date | undefined | null;
  endDateTo?: Date | undefined | null;
  includeCurrentUserHasRead?: boolean | undefined | null;
  includeSystemAnnouncements?: boolean | undefined | null;
}

const defaultValues: DefinedPropertyObject<AnnouncementFilterForm> = {
  types: null,
  current: false,
  startDateFrom: null,
  startDateTo: null,
  endDateFrom: null,
  endDateTo: null,
  includeCurrentUserHasRead: true,
  includeSystemAnnouncements: false,
  ...CombinedModificationRecordEntityDefaultFormValues,
};
export const announcementsFormToFilters = (form: AnnouncementFilterForm): AnnouncementFilters => {
  let filters: AnnouncementFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
  };
  filters.types = Array.isArray(form.types) && form.types.length > 0 ? form.types.map((t) => t.id) : null;
  filters.current = form.current || false;
  filters.startDateFrom = toValidDate(form.startDateFrom);
  filters.startDateTo = toValidDate(form.startDateTo);
  filters.endDateFrom = toValidDate(form.endDateFrom);
  filters.endDateTo = toValidDate(form.endDateTo);
  filters.includeCurrentUserHasRead = form.includeCurrentUserHasRead || true;
  filters.includeSystemAnnouncements = form.includeSystemAnnouncements || false;
  return filters;
};

/**
 * Converts AnnouncementFilters to AnnouncementFilterForm
 * @param filters - The filters to convert
 * @returns The converted form
 * @author @CorradoSurmanowicz
 */
export const announcementsFiltersToForm = (filters: AnnouncementFilters): AnnouncementFilterForm => {
  return {
    types: filters.types ? filters.types.map((id) => ({ id, name: id })) : null,
    current: filters.current || false,
    startDateFrom: filters.startDateFrom ? new Date(filters.startDateFrom) : null,
    startDateTo: filters.startDateTo ? new Date(filters.startDateTo) : null,
    endDateFrom: filters.endDateFrom ? new Date(filters.endDateFrom) : null,
    endDateTo: filters.endDateTo ? new Date(filters.endDateTo) : null,
    includeCurrentUserHasRead: filters.includeCurrentUserHasRead || true,
    includeSystemAnnouncements: filters.includeSystemAnnouncements || false,
    ...combinedModificationRecordEntityFiltersToForm(filters),
  };
};

const defaultFilters: AnnouncementFilters = {
  sortBy: [{ property: "TYPE", sortDirection: "DESC" }],
  includeCurrentUserHasRead: true,
};

export const AnnouncementFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<Announcement, AnnouncementFilterForm, AnnouncementFilters>) => {
  const { session } = useContext(SessionContext);

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultFilters}
      formToFilters={announcementsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
    >
      {({ control, onReset }) => (
        <EntityFilterSideBar onReset={onReset}>
          <SelectFormField
            id="types"
            label={announcementFieldLabels.type}
            control={control}
            isMulti
            horizontal={false}
            items={AnnouncementTypes?.map((x) => ({ id: x, name: x })) ?? []}
            placeholder="Select announcement type..."
          />

          <SplitDatePickerFormField
            control={control}
            idStart="startDateFrom"
            idEnd="startDateTo"
            label="Start date (from - to)"
            horizontal={false}
            showTimeSelect
          />

          <SplitDatePickerFormField
            control={control}
            idStart="endDateFrom"
            idEnd="endDateTo"
            label="Expiration date (from - to)"
            horizontal={false}
            showTimeSelect
          />

          <ToggleFormField id="current" control={control} label="Only active" horizontal />
          <ToggleFormField id="includeCurrentUserHasRead" control={control} label="Include already read" horizontal />
          {session?.permissions.logsadmin && (
            <ToggleFormField
              id="includeSystemAnnouncements"
              control={control}
              label="Include system announcements"
              horizontal
            />
          )}

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
